import React, { useEffect, useRef, useState } from 'react';
import { useContent } from '../i18n/LanguageContext';
import { TrendingUp, Clock, Wallet } from 'lucide-react';

const fmtCHF = (n) => new Intl.NumberFormat('fr-CH', { maximumFractionDigits: 0 }).format(Math.max(0, Math.round(n)));
const ease = (x) => 1 - Math.pow(1 - x, 3);

export default function SavingsCounter() {
  const c = useContent();
  const s = c.savings;
  const cfg = c.roi.cfg;

  // Mirror the ROI calculator formula on a typical default deployment.
  const laborAnnual = cfg.hours.default * cfg.hourlyCost * 52;
  const timeSaved = laborAnnual * cfg.timeReduction;
  const fleetValue = cfg.assets.default * cfg.avgValue.default;
  const lossAvoided = fleetValue * (cfg.annualLossRate * cfg.lossReduction + cfg.utilRate * cfg.utilReduction);
  const cost = cfg.assets.default * cfg.tagMonthly * 12;
  const chfTarget = Math.max(0, timeSaved + lossAvoided - cost);
  const hoursTarget = cfg.hours.default * 52 * cfg.timeReduction;

  const [chf, setChf] = useState(0);
  const [hrs, setHrs] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  const chfRef = useRef(0);
  const hrsRef = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const dur = 2200, t0 = performance.now();
          const tick = (now) => {
            const p = Math.min(1, (now - t0) / dur);
            const k = ease(p);
            chfRef.current = chfTarget * k;
            hrsRef.current = hoursTarget * k;
            setChf(chfRef.current);
            setHrs(hrsRef.current);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [chfTarget, hoursTarget]);

  // Keep the counter alive: savings accrue continuously through the year.
  useEffect(() => {
    const perSec = chfTarget / (8 * 3600);
    const hPerSec = hoursTarget / (8 * 3600);
    const id = setInterval(() => {
      if (!started.current || chfRef.current < chfTarget) return;
      chfRef.current += perSec;
      hrsRef.current += hPerSec;
      setChf(chfRef.current);
      setHrs(hrsRef.current);
    }, 250);
    return () => clearInterval(id);
  }, [chfTarget, hoursTarget]);

  return (
    <section id="savings" ref={ref} className="py-16 md:py-24 bg-slate-900 text-white overflow-hidden">
      <div className="lt-container relative">
        <div className="absolute inset-0 grid-dots opacity-[0.07]" />
        <div className="relative max-w-3xl reveal">
          <div className="inline-flex items-center gap-2 rounded-full bg-teal-500/15 border border-teal-500/25 px-3 py-1 mb-5">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-teal-400" />
            </span>
            <span className="text-xs font-semibold uppercase tracking-widest text-teal-300">{s.eyebrow} · {s.live}</span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight">{s.title}</h2>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl">{s.subtitle}</p>
        </div>

        <div className="relative mt-12 grid md:grid-cols-2 gap-6 reveal">
          <Counter
            icon={Wallet}
            value={fmtCHF(chf)}
            unit="CHF"
            label={s.chf}
            hint={s.chfHint}
            testid="savings-chf"
          />
          <Counter
            icon={Clock}
            value={fmtCHF(hrs)}
            unit="h"
            label={s.hours}
            hint={s.hoursHint}
            testid="savings-hours"
          />
        </div>

        <div className="relative mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 reveal">
          {s.stats.map((st, i) => (
            <div key={i} data-testid={`savings-stat-${i}`} className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4 flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-teal-400 shrink-0" />
              <div>
                <span className="font-display text-2xl font-bold text-teal-300">{st.value}</span>
                <span className="ml-2 text-sm text-slate-300">{st.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Counter({ icon: Icon, value, unit, label, hint, testid }) {
  return (
    <div data-testid={testid} className="relative rounded-3xl bg-gradient-to-br from-white/[0.08] to-white/[0.02] border border-white/10 p-7 md:p-9 overflow-hidden">
      <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-teal-500/20 blur-2xl" />
      <div className="relative flex items-center gap-2 text-teal-300">
        <Icon className="w-5 h-5" />
        <span className="text-sm font-semibold uppercase tracking-widest">{label}</span>
      </div>
      <div className="relative mt-4 flex items-end gap-2">
        <span className="font-display text-5xl md:text-6xl font-bold text-gradient tabular-nums">{value}</span>
        <span className="mb-2 text-xl font-semibold text-slate-300">{unit}</span>
      </div>
      <p className="relative mt-3 text-sm text-slate-400">{hint}</p>
    </div>
  );
}
